import { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useFieldArray, useWatch } from 'react-hook-form';
import { PiPlusBold, PiTrashBold } from 'react-icons/pi';

import { cn } from '#/lib/utils';
import {
  Button,
  DESTRUCTIVE_GHOST_BUTTON_CLASS,
} from '#/modules/commonComponents/button';
import FloatingInput from '#/modules/commonComponents/form/FloatingInput';
import MtrPickerDialog from '#/modules/imsMgmt/settings/mtr/components/MtrPickerDialog';
import {
  collectUsedMtrIds,
  mapPickerRowToExtraMaterial,
} from '#/modules/momMgmt/settings/bom/helper/bomGroupsForm';

const BomExtraMaterialsEditor = ({
  control,
  register,
  errors,
}) => {
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'extraMaterials',
  });
  const extraMaterials = useWatch({
    control,
    name: 'extraMaterials',
    defaultValue: [],
  });
  const groups = useWatch({
    control,
    name: 'groups',
    defaultValue: [],
  });

  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerKey, setPickerKey] = useState(0);

  const pickerInitialRows = useMemo(
    () => (extraMaterials ?? [])
      .filter((item) => item?.mtrId)
      .map((item) => ({
        optionMtrId: item.mtrId,
        mtrNo: item.mtrNo ?? '',
        name: item.mtrName ?? '',
        status: '',
      })),
    [extraMaterials],
  );

  function handleOpenPicker() {
    setPickerKey((prev) => prev + 1);
    setPickerOpen(true);
  }

  function handlePickerConfirm(selectedRows) {
    const existing = new Set(
      (extraMaterials ?? []).map((item) => item?.mtrId).filter(Boolean),
    );
    const groupIds = collectUsedMtrIds(groups);

    (selectedRows ?? [])
      .map(mapPickerRowToExtraMaterial)
      .filter((item) => {
        if (!item.mtrId || existing.has(item.mtrId)) {
          return false;
        }
        return !groupIds.has(item.mtrId);
      })
      .forEach((item) => {
        append(item);
      });
  }

  return (
    <section className="form-section @container/form-section min-w-0">
      <div className="form-section__header">
        <h2 className="form-block__title mb-0! mr-2">附加物料</h2>
        <Button type="button" size="sm" onClick={handleOpenPicker}>
          <PiPlusBold aria-hidden />
          新增
        </Button>
      </div>

      {errors?.extraMaterials?.message ? (
        <p className="mt-2 text-sm text-red-500">{errors.extraMaterials.message}</p>
      ) : null}

      <div className="form-section__scroll">
        {fields.length === 0 ? (
          <p className="py-4 text-center text-sm text-muted-foreground">
            尚未設定附加物料
          </p>
        ) : (
          <div className="space-y-2">
            <div
              className={cn(
                'grid grid-cols-[minmax(0,1fr)_6.5rem_2.5rem] gap-2',
                'px-1 text-xs font-medium text-slate-500',
              )}
            >
              <p>物料</p>
              <p>數量</p>
              <p className="text-center">操作</p>
            </div>

            {fields.map((field, index) => {
              const item = extraMaterials?.[index];

              return (
                <div
                  key={field.id}
                  className={cn(
                    'grid grid-cols-[minmax(0,1fr)_6.5rem_2.5rem] items-start gap-2',
                    'rounded-md border border-slate-200 bg-slate-50 px-2 py-2',
                  )}
                >
                  <div className="min-w-0 pt-1">
                    <p className="truncate text-sm font-medium text-gray-800">
                      {item?.mtrName || '（未命名）'}
                    </p>
                    <p className="truncate text-xs text-muted-foreground">
                      {item?.mtrNo || item?.mtrId || '—'}
                    </p>
                    <input
                      type="hidden"
                      {...register(`extraMaterials.${index}.mtrId`)}
                    />
                    <input
                      type="hidden"
                      {...register(`extraMaterials.${index}.mtrNo`)}
                    />
                    <input
                      type="hidden"
                      {...register(`extraMaterials.${index}.mtrName`)}
                    />
                  </div>
                  <FloatingInput
                    id={`extraMaterials.${index}.qty`}
                    type="number"
                    label="數量"
                    register={register}
                    errors={errors}
                    containerClassName="mb-0!"
                  />
                  <div className="flex justify-center pt-2">
                    <Button
                      type="button"
                      size="icon-sm"
                      variant="ghost"
                      className={DESTRUCTIVE_GHOST_BUTTON_CLASS}
                      aria-label="移除附加物料"
                      onClick={() => remove(index)}
                    >
                      <PiTrashBold className="size-4" aria-hidden />
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <MtrPickerDialog
        key={pickerKey}
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        initialSelectedRows={pickerInitialRows}
        onConfirm={handlePickerConfirm}
        title="請選擇附加物料"
        description="多選後確認；已在附加清單或物料位置群組內者不重複加入。"
      />
    </section>
  );
};

BomExtraMaterialsEditor.propTypes = {
  control: PropTypes.object.isRequired,
  register: PropTypes.func.isRequired,
  errors: PropTypes.object,
};

export default BomExtraMaterialsEditor;
